import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import {
  Box,
  Dialog,
  Grid,
  IconButton,
  Skeleton,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import { useAppSelector } from '../../../../../../../../app/redux/hooks';
import carImg from '../../../../../../../../app/assets/images/vehicles/car.png';
import bikeImg from '../../../../../../../../app/assets/images/vehicles/bike.png';
import busImg from '../../../../../../../../app/assets/images/vehicles/bus.png';
import truckImg from '../../../../../../../../app/assets/images/vehicles/truck.png';
import scootyImg from '../../../../../../../../app/assets/images/vehicles/scooty.png';
import comfyImg from '../../../../../../../../app/assets/images/vehicles/comfy.png';
import profileImg from '../../../../../../../../app/assets/images/profile.png';
import GoogleMap from '../../../../../../../components/maps/googlemap/GoogleMap';
import {
  capitalizeFirstLetter,
  epochToDateFormat
} from '../../../../../../../../utils/commonFunctions';
import { useVehicleDetailsHooks } from './hooks/VehicleDetailsHooks';
import Chart from '../../../../../../../components/chart/Chart';
import CustomButton from '../../../../../../../components/buttons/CustomButton';
import LoginRedirect from '../../../../../../../components/loginredirect/LoginRedirect';
import CustomIconButton from '../../../../../../../components/buttons/CustomIconButton';
import { config } from 'react-spring';
import Carousel from 'react-spring-3d-carousel';
import GoogleMarkers from '../../../../../../../components/maps/googlemarkers/GoogleMarkers';
import { ChartLoader, PieCenterLabel } from './components/VehicleDetailsloader';
import UpdateYaantrac from '../../UpdateYaantrac';
import './VehicleDetails.scss';
import { useDrawingArea } from '@mui/x-charts';

interface Vehicle {
  id: number;
  vehicleNumber: string;
  vehicleType: string;
  driverName: string;
  fuelType: string;
  vehicleCategory: string;
  transmissionType: string;
  VehicleMake: string;
  seatingCapacity: number | null;
  numberOfAxles: number | null;
}

interface VehicleDetails {
  dotInspectionDate: null | number;
  driveType: string;
  emissionStandard: string;
  engineCapacity: string;
  engineType: string;
  freewaySpeedLimit: number;
  fuelCapacity: number;
  fuelType: string;
  id: number;
  insuranceExpiryDate: null | number;
  insuranceName: null | string;
  isActive: number;
  lastServicedDate: number;
  manufacturedYear: string;
  modifiedAt: number;
  modifiedBy: number;
  nonFreewaySpeedLimit: number;
  policyNumber: null | string;
  registeredState: null | string;
  subType: string;
  transmissionType: string;
  vehicleCategory: string;
  vehicleChassisNumber: string;
  vehicleColor: string;
  vehicleEngineNumber: string;
  vehicleExpirationDate: number;
  vehicleIdentificationNumber: null | string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleNumber: string;
  vehicleRegistrationDate: number;
  vehicleType: string;
  yearOfPurchase: null | number;
}

interface SelectedVehicleData {
  vehicleModel: string;
  vehicleColor: string;
  vehicleCategory: string;
  lastServicedDate: number | null;
  yearOfPurchase: string | null;
  insuranceExpiryDate: number | null;
}

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  vehicleDetails: Vehicle | null;
}

const VehicleDetails = ({ isOpen, setIsOpen, vehicleDetails }: Props) => {
  const vehicleData = useAppSelector(state => state.singleVehcileDetails);

  const {
    handleCloseDetails,
    details,
    open,
    setOpen,
    secondaryDetails,
    distanceDetails,
    insertSpaceBeforeUppercase
  } = useVehicleDetailsHooks({
    setIsOpen,
    vehicleDetails,
    vehicleData
  });

  const loaderChart = [{ value: '100', color: '#ccc', label: 'Loading...' }];

  const data = [{ value: details?.fuelCapacity, color: 'red', label: 'Fuel Capacity' }];

  const kmData = [
    ...(details?.harshAcceleration === 0 &&
    details?.harshBreaking === 0 &&
    details?.harshCornering === 0
      ? [{ value: 1, color: '#ccc', label: 'No Harsh Events' }]
      : [
          { value: details?.harshBreaking, color: '#ff6f22', label: 'Harsh Braking' },
          {
            value: details?.harshAcceleration,
            color: '#a9acff',
            label: 'Harsh Acceleration'
          },
          {
            value: details?.harshCornering,
            color: '#84ee44',
            label: 'Harsh Cornering'
          }
        ])
  ];

  const batteryData = [
    { value: details?.batteryLevel, color: '#22c971', label: 'Battery' },
    ...(details?.batteryLevel !== 100
      ? [{ value: details?.batteryLevel - 100, color: '#ccc' }]
      : [])
  ];

  const size = {
    width: 200,
    height: 100
  };

  const StyledText = styled('text')(({ theme }) => ({
    fill: (theme as any).palette.text.primary,
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fontSize: 12,
    fontWeight: 600
  }));

  function PieCenterLabel({
    children,
    color
  }: {
    children: React.ReactNode;
    color: string;
  }) {
    const { width, height, left, top } = useDrawingArea();
    return (
      <StyledText
        x={left + width / 2}
        y={top + height / 2}
        style={{ fill: color || 'inherit' }}
      >
        {children}
      </StyledText>
    );
  }

  const ChartLoader = () => (
    <Box className='chart-loader'>
      <Skeleton variant='text' sx={{ fontSize: '20px' }} />
      <Skeleton variant='rounded' className='chart-yaxis' />
      <Box className='loader-body'>
        {[...Array(8)].map((_, index) => (
          <Skeleton key={index} variant='text' className='chart-loader-content' />
        ))}
      </Box>
    </Box>
  );

  const [goToSlide, setGoToSlide] = useState<any>(null);

  const cards =
    details?.driverDetailResponse?.length > 0
      ? details?.driverDetailResponse?.map((item: any, index: any) => ({
          key: `driver-${index}`,
          content: (
            <Box>
              <Tooltip title={capitalizeFirstLetter(item?.driverName ?? 'N/A')}>
                <Typography className='content name' noWrap sx={{ width: '200px' }}>
                  Name: {capitalizeFirstLetter(item?.driverName ?? 'N/A')}
                </Typography>
              </Tooltip>
              <Tooltip title={item?.contactPhone}>
                <Typography className='content' noWrap sx={{ width: '200px' }}>
                  Mobile No.:
                  {item?.contactPhone ?? 'N/A'}
                </Typography>
              </Tooltip>
              <Tooltip title={item?.contactEmail}>
                <Typography className='content' noWrap sx={{ width: '200px' }}>
                  Email:{item?.contactEmail ?? 'N/A'}
                </Typography>
              </Tooltip>
            </Box>
          ),
          onClick: () => setGoToSlide(index)
        }))
      : [];

  return (
    <>
      <Dialog
        open={isOpen}
        fullScreen
        onClose={() => handleCloseDetails()}
        sx={{ margin: '20px' }}
      >
        <Box className='vehicle-details-container'>
          <Box className='header-view'>
            <Typography className='heading'>Vehicle Details</Typography>
            <Box className='header-end'>
              {!vehicleData?.isLoading && (
                <Box className='update-btn'>
                  <CustomButton
                    category='Update'
                    className='saveChanges'
                    onClick={() => setOpen(true)}
                    endIcon={<Icon icon='tabler:edit' className='close-icon' />}
                  />
                </Box>
              )}

              <Box className='header-close' onClick={() => handleCloseDetails()}>
                <Icon icon='ic:round-close' className='close-icon' />
              </Box>
            </Box>
          </Box>
          <Box className='vehcile-dialog-container'>
            <Grid container spacing={1} className='vehicle-details-component'>
              <Grid item md={12} lg={8.5}>
                <Grid container className='vehicle-info'>
                  <Grid item md={8} sm={12}>
                    <Box className='vehicle-3d'>
                      <Box className='chart-details'>
                        <PieChart
                          series={[
                            {
                              data: vehicleData.isLoading ? loaderChart : data,
                              innerRadius: 35,
                              paddingAngle: 5,
                              cornerRadius: 5
                            }
                          ]}
                          legend={{ hidden: true }}
                          {...size}
                        >
                          <PieCenterLabel color='#ff5722'>
                            {details?.fuelCapacity !== null
                              ? `Fuel ${
                                  vehicleData.isLoading ? 0 : details?.fuelCapacity
                                } L`
                              : 'Fuel N/A'}
                          </PieCenterLabel>
                        </PieChart>
                        <PieChart
                          series={[
                            {
                              data: vehicleData.isLoading ? loaderChart : kmData,
                              innerRadius: 35,
                              paddingAngle: 5,
                              cornerRadius: 5
                            }
                          ]}
                          slotProps={{
                            popper: {
                              sx: { display: kmData[0].value === 1 ? 'none' : 'auto' }
                            }
                          }}
                          legend={{ hidden: true }}
                          {...size}
                        >
                          <PieCenterLabel color='#3239ea'>A / B / C</PieCenterLabel>
                        </PieChart>
                        <PieChart
                          series={[
                            {
                              data: vehicleData.isLoading ? loaderChart : batteryData,
                              innerRadius: 35,
                              paddingAngle: 5,
                              cornerRadius: 5
                            }
                          ]}
                          legend={{ hidden: true }}
                          {...size}
                        >
                          <PieCenterLabel color='#22c971'>
                            ⚡︎ {vehicleData.isLoading ? 0 : details?.batteryLevel}%
                          </PieCenterLabel>
                        </PieChart>
                      </Box>
                      <Box className='vehicle-display'>
                        <Box className='vehicle-speed'>
                          <Typography className='count'>
                            {details?.freewaySpeedLimit}
                            <span className='count-value'>Km/hr</span>
                          </Typography>
                          <Typography className='text'>Free way</Typography>
                        </Box>
                        <Box className='vehicle-img-details'>
                          <Box
                            component={'img'}
                            src={
                              vehicleDetails?.vehicleType === 'truck'
                                ? truckImg
                                : vehicleDetails?.vehicleType === 'bike'
                                ? bikeImg
                                : vehicleDetails?.vehicleType === 'bus' ||
                                  vehicleDetails?.vehicleType === 'coach'
                                ? busImg
                                : vehicleDetails?.vehicleType === 'scooter'
                                ? scootyImg
                                : vehicleDetails?.vehicleType === 'comfy'
                                ? comfyImg
                                : carImg
                            }
                            className='vehicle-img'
                          />
                        </Box>
                        <Box className='vehicle-speed'>
                          <Typography className='count'>
                            {details?.nonFreewaySpeedLimit}
                            <span className='count-value'>Km/hr</span>
                          </Typography>
                          <Typography className='text'>Non free way</Typography>
                        </Box>
                      </Box>
                      <Box className='vehicle-no'>
                        <Typography className='vehicle-num'>
                          {vehicleDetails?.vehicleNumber}
                        </Typography>
                        <Typography className='vehicle-make'>
                          {vehicleData.isLoading ? 'loading...' : details?.vehicleMake}
                        </Typography>
                      </Box>
                      <Box className='vehicle-card-footer'>
                        <Box className='footer-content'>
                          <Icon className='icon' icon='lucide-lab:gearbox' />
                          <Typography className='content'>
                            {vehicleData.isLoading
                              ? 'loading...'
                              : details?.transmissionType
                              ? details?.transmissionType
                              : '-'}
                          </Typography>
                        </Box>
                        <Box className='footer-content'>
                          <Icon className='icon' icon='mdi:fuel-pump' />
                          <Typography className='content'>
                            {vehicleData.isLoading
                              ? 'loading...'
                              : details?.fuelType
                              ? details?.fuelType
                              : '-'}
                          </Typography>
                        </Box>
                        <Box className='footer-content'>
                          <Icon
                            className='icon'
                            icon={
                              vehicleDetails?.numberOfAxles === null
                                ? 'mdi:seat-passenger'
                                : 'mingcute:four-wheel-drive-fill'
                            }
                          />
                          <Typography className='content'>
                            {vehicleData.isLoading
                              ? 'loading...'
                              : vehicleDetails?.seatingCapacity !== null
                              ? vehicleDetails?.seatingCapacity
                              : vehicleDetails?.numberOfAxles !== null
                              ? vehicleDetails?.numberOfAxles
                              : '-'}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item sm={12} md={4} className='extra-details'>
                    <Box className='vehicle-map'>
                      <GoogleMap
                        showMapOptions={false}
                        isShowFullScreen={true}
                        children={<GoogleMarkers />}
                      />
                    </Box>
                    <Box className='more-details'>
                      {vehicleData.isLoading ? (
                        <>
                          {[...Array(6)].map((_, index) => (
                            <Box className='loader-body' key={index}>
                              <Skeleton variant='text' className='loader-body-content' />
                              <Skeleton variant='text' className='loader-body-content' />
                            </Box>
                          ))}
                        </>
                      ) : (
                        <TableContainer className='table'>
                          <Table aria-label='simple table'>
                            <TableBody>
                              {secondaryDetails?.map(
                                (row: SelectedVehicleData, rowIndex: number) => (
                                  <React.Fragment key={`row-${rowIndex}`}>
                                    {Object.entries(row).map(([key, value]) => (
                                      <React.Fragment key={key}>
                                        {value && (
                                          <TableRow
                                            sx={{
                                              '&:last-child td, &:last-child th': {
                                                border: 0
                                              }
                                            }}
                                          >
                                            <TableCell className='dataKeys'>
                                              {capitalizeFirstLetter(
                                                insertSpaceBeforeUppercase(key)
                                              )}
                                            </TableCell>
                                            <TableCell
                                              className={`dataValues dataCells ${key}`}
                                            >
                                              {capitalizeFirstLetter(value)}
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </React.Fragment>
                                    ))}
                                  </React.Fragment>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item md={12} lg={3.5}>
                <Grid container className='vehicle-info-right'>
                  <Grid item sm={12}>
                    <Box className='distance-chart'>
                      {vehicleData.isLoading ? (
                        <ChartLoader />
                      ) : (
                        <Chart options={distanceDetails} showTypography={false} />
                      )}
                    </Box>
                  </Grid>
                  <Grid item sm={12}>
                    <Box className='trip-details'>
                      <Box className='trip-status'>
                        <Typography className='count expired'>
                          {vehicleData.isLoading ? 0 : details?.tripsExpired}
                        </Typography>
                        <Typography className='content expired'>Expired</Typography>
                      </Box>
                      <Box className='trip-status'>
                        <Typography className='count pending'>
                          {vehicleData.isLoading ? 0 : details?.tripsPending}
                        </Typography>
                        <Typography className='content pending'>Scheduled</Typography>
                      </Box>
                      <Box className='trip-status'>
                        <Typography className='count completed'>
                          {vehicleData.isLoading ? 0 : details?.tripsCompleted}
                        </Typography>
                        <Typography className='content completed'>Completed</Typography>
                      </Box>
                      <Box className='trip-status'>
                        <Typography className='count incomplete'>
                          {vehicleData.isLoading ? 0 : details?.tripsInCompleted ?? 0}
                        </Typography>
                        <Typography className='content incomplete'>Incomplete</Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Box className='driver-info-container'>
              <Grid container spacing={2} alignItems={'center'}>
                <Grid item lg={4} position={'relative'}>
                  {cards?.length > 1 && (
                    <Box sx={{ position: 'absolute', top: '45%', left: '5px' }}>
                      {/* <CustomIconButton
                        category='arrowLeft'
                        showTooltip={false}
                        onClick={() => {
                          setGoToSlide(goToSlide - 1);
                        }}
                      /> */}
                      <IconButton
                        onClick={() => {
                          setGoToSlide(goToSlide - 1);
                        }}
                      >
                        <Icon
                          icon='iconamoon:arrow-left-6-circle-fill'
                          width={30}
                          height={30}
                          color='#c3c3c3'
                        />
                      </IconButton>
                    </Box>
                  )}
                  <Box className='driver-profile'>
                    <Box className='profile'>
                      <Box className='active-status-component '>
                        <Box component={'img'} src={profileImg} className='profile-img' />
                      </Box>
                    </Box>
                    {vehicleData.isLoading ? (
                      <Box className='loader-body3'>
                        <Skeleton variant='text' className='loader-body-content' />
                        <Skeleton variant='text' className='loader-body-content' />
                        <Skeleton variant='text' className='loader-body-content' />
                      </Box>
                    ) : (
                      <>
                        <Box className='vehicle-carousel-box'>
                          <Carousel
                            slides={cards}
                            goToSlide={goToSlide}
                            offsetRadius={1}
                            showNavigation={false}
                            animationConfig={config.gentle}
                          />
                        </Box>
                      </>
                    )}
                  </Box>
                  {cards?.length > 1 && (
                    <Box sx={{ position: 'absolute', top: '45%', right: '-10px' }}>
                      {/* <CustomIconButton category='arrowRight' showTooltip={false} /> */}
                      <IconButton
                        onClick={() => {
                          setGoToSlide(goToSlide + 1);
                        }}
                      >
                        <Icon
                          icon='iconamoon:arrow-right-6-circle-fill'
                          width={30}
                          height={30}
                          color='#c3c3c3'
                        />
                      </IconButton>
                    </Box>
                  )}
                </Grid>
                <Grid item lg={3} className='divider'>
                  {vehicleData.isLoading ? (
                    <>
                      {[...Array(6)].map((_, index) => (
                        <Box className='loader-body2' key={index}>
                          <Skeleton variant='text' className='loader-body-content' />
                          <Skeleton variant='text' className='loader-body-content' />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <>
                      <Typography className='content1'>
                        Vehicle Engine Number :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.vehicleEngineNumber
                            ? details?.vehicleEngineNumber?.toUpperCase()
                            : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Vehicle Registration Date :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.vehicleRegistrationDate
                            ? epochToDateFormat(details?.vehicleRegistrationDate / 1000)
                            : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Vehicle Expiration Date :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.vehicleExpirationDate
                            ? epochToDateFormat(details?.vehicleExpirationDate / 1000)
                            : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Vehicle Chassis Number :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.vehicleChassisNumber
                            ? details?.vehicleChassisNumber?.toUpperCase()
                            : 'N/A'}
                        </span>
                      </Typography>
                      {details?.vehicleIdentificationNumber && (
                        <Typography className='content1'>
                          Vehicle Id Number :{' '}
                          <span style={{ color: '#232323' }}>
                            {details?.vehicleIdentificationNumber
                              ? details?.vehicleIdentificationNumber?.toUpperCase()
                              : 'N/A'}
                          </span>
                        </Typography>
                      )}
                    </>
                  )}
                </Grid>
                <Grid item lg={2} className='divider'>
                  {vehicleData.isLoading ? (
                    <>
                      {[...Array(6)].map((_, index) => (
                        <Box className='loader-body2' key={index}>
                          <Skeleton variant='text' className='loader-body-content' />
                          <Skeleton variant='text' className='loader-body-content' />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <>
                      {details?.subtype && (
                        <Typography className='content1'>
                          Subtype :{' '}
                          <span style={{ color: '#232323' }}>{details?.subtype}</span>
                        </Typography>
                      )}
                      <Typography className='content1'>
                        Engine Type :{' '}
                        <span style={{ color: '#232323' }}>{details?.engineType}</span>
                      </Typography>
                      <Typography className='content1'>
                        Engine Capacity :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.engineCapacity}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Emission Standard :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.emissionStandard}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Drive Type :{' '}
                        <span style={{ color: '#232323' }}>{details?.driveType}</span>
                      </Typography>
                    </>
                  )}
                </Grid>
                <Grid item lg={3}>
                  {vehicleData.isLoading ? (
                    <>
                      {[...Array(6)].map((_, index) => (
                        <Box className='loader-body2' key={index}>
                          <Skeleton variant='text' className='loader-body-content' />
                          <Skeleton variant='text' className='loader-body-content' />
                        </Box>
                      ))}
                    </>
                  ) : (
                    <>
                      <Typography className='content1'>
                        Dot Inspection Date :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.dotInspectionDate
                            ? epochToDateFormat(details?.dotInspectionDate / 1000)
                            : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Insurance Name :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.insuranceName ? details?.insuranceName : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Policy Number :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.policyNumber ? details?.policyNumber : 'N/A'}
                        </span>
                      </Typography>
                      <Typography className='content1'>
                        Registered State :{' '}
                        <span style={{ color: '#232323' }}>
                          {details?.registeredState ? details?.registeredState : 'N/A'}
                        </span>
                      </Typography>
                    </>
                  )}
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
        <UpdateYaantrac
          open={open}
          setOpen={setOpen}
          roleType={'Update'}
          filteredRow={{ row: details }}
        />
      </Dialog>
      <LoginRedirect />
    </>
  );
};

export default VehicleDetails;
